/**
 * The ONE "should Terra re-ask Clay about this dispatch-log row?" rule
 * (PR #1715 review, finding 5).
 *
 * Shared by the server re-check (`dispatch-recheck.ts#isRecheckCandidate`,
 * behind `POST …/dispatch-log/recheck`) and the inspector
 * (`dispatch-rep-status.ts` — the stale pill and the automatic re-check on
 * open). Before this module each side had its own windows (the server 60s for
 * all three statuses, the client ~21 min / 30 min), so the client could ask
 * for a re-check the server then refused.
 *
 * DOM-free and zod-free ON PURPOSE: the browser bundle reads it, so it
 * imports nothing but a timing number.
 *
 * # The rule
 *
 *   - only a `result = 'sent'` row (Clay has the send);
 *   - `clay_not_found` → always. The status poll's give-up write is its LAST
 *     statement, so nothing is watching the row any more — and that write
 *     stamps `result_at = now()`, which is why no min age applies here;
 *   - `clay_pending` / `clay_running` → only once Clay's last word
 *     ({@link rowStatusAnchor}: `result_at`, else when the row's START
 *     actually began, `processing_at`, else `dispatched_at`) is
 *     {@link RECHECK_STALE_AFTER_MS} old — before that the poll may still be
 *     watching it;
 *   - anything else is settled.
 */
import { CLAY_POLL_BUDGET_MS } from '../_shared/clay-run-status-timing';

/** The poll's give-up budget plus one minute — past this, nothing else will move a pending/running row. */
export const RECHECK_STALE_AFTER_MS = CLAY_POLL_BUDGET_MS + 60_000;

export type RecheckStatus = 'clay_not_found' | 'clay_pending' | 'clay_running';

/**
 * The ONE "when did this row last move?" timestamp every liveness window ages
 * a row from (plan 2026-10-14-002 U0d): Clay's last word (`result_at`), else
 * when the row's START actually began (`processing_at`), else when the row was
 * written (`dispatched_at`). A paced send writes every row at once and starts
 * its last member ~50 minutes later (3,000 people at 60/min), so ageing by
 * `dispatched_at` alone would call a just-started row stale. The server's SQL
 * twin is `dispatch-recheck.ts#STATUS_ANCHOR_SQL`.
 */
export function rowStatusAnchor(row: {
  result_at: string | null;
  processing_at: string | null;
  dispatched_at: string | null;
}): string | null {
  return row.result_at ?? row.processing_at ?? row.dispatched_at;
}

/**
 * `ageMs` is the time since {@link rowStatusAnchor}, or `null` when unknown.
 */
export function isRecheckEligible(
  row: { result: string | null; result_status: string | null },
  ageMs: number | null,
): boolean {
  if (row.result !== 'sent') return false;
  if (row.result_status === 'clay_not_found') return true;
  if (row.result_status === 'clay_pending' || row.result_status === 'clay_running') {
    return ageMs !== null && ageMs >= RECHECK_STALE_AFTER_MS;
  }
  return false;
}
